import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Button, Alert, Breadcrumbs, Link, Typography, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import SimplePDFViewer from '../components/common/SimplePDFViewer';
import Hero from '../components/common/Hero';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import DescriptionIcon from '@mui/icons-material/Description';
import { documentService, getStrapiMediaUrl } from '../services/api';

const DocumentViewer = () => {
  const { documentName } = useParams();
  const navigate = useNavigate();
  const [documentInfo, setDocumentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Legacy document metadata (fallback)
  const fallbackDocuments = [
    {
      id: 'covenant',
      title: "Brynleigh Estates Covenants",
      description: "The foundational covenants established for Brynleigh Estates, revised on June 19, 2013.",
      fileName: "Brynleigh_Estates_Covenant_Revised_06192013.pdf"
    },
    {
      id: 'bylaws',
      title: "HOA Bylaws",
      description: "The governing bylaws for the Brynleigh Estates Homeowners Association, revised on June 19, 2013.",
      fileName: "Brynleigh_Estates_ByLaws_Revised_06192013.pdf"
    },
    {
      id: 'articles',
      title: "Articles of Incorporation",
      description: "Legal articles of incorporation for the Brynleigh Estates Homeowners Association.",
      fileName: "Brynleigh_Estates_Articles_Incorporation.pdf"
    },
    {
      id: 'minutes',
      title: "2024 Annual Meeting Minutes",
      description: "Minutes from the 2024 Annual HOA meeting held on February 5, 2024.",
      fileName: "Brynleigh_Estates_2024_Annual_Meeting_Minutes.pdf"
    }
  ];

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        // Check if documentName is a numeric ID (from the API) or a legacy ID
        const isNumericId = !isNaN(parseInt(documentName));
        
        if (isNumericId) {
          // Fetch from API using ID
          const response = await documentService.getById(documentName);
          
          if (response && response.data) {
            const doc = response.data;
            const documentUrl = doc.attributes.document?.data 
              ? getStrapiMediaUrl(doc.attributes.document.data.attributes.url)
              : null;
              
            if (documentUrl) {
              setDocumentInfo({
                id: doc.id,
                title: doc.attributes.title,
                description: doc.attributes.description || '',
                documentUrl: documentUrl,
                fileName: doc.attributes.document.data.attributes.name
              });
              setError(null);
            } else {
              setDocumentInfo(null);
              setError("Document file not found. Please contact the administrator.");
            }
          } else {
            setDocumentInfo(null);
            setError("Document not found. Please return to the documents page.");
          }
        } else {
          // Use legacy fallback documents
          const doc = fallbackDocuments.find(doc => doc.id === documentName);
          
          if (doc) {
            setDocumentInfo({
              ...doc,
              documentUrl: `/documents/${doc.fileName}`
            });
            setError(null);
          } else {
            setDocumentInfo(null);
            setError("Document not found. Please return to the documents page.");
          }
        }
      } catch (err) {
        console.error('Error fetching document:', err);
        setDocumentInfo(null);
        setError("Failed to load the document. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentName]);

  return (
    <>
      <Helmet>
        <title>{documentInfo ? `${documentInfo.title} | Brynleigh Estates` : 'Document Viewer | Brynleigh Estates'}</title>
        <meta 
          name="description" 
          content={documentInfo ? documentInfo.description : "View important HOA documents for Brynleigh Estates."} 
        />
      </Helmet>
      
      <Hero 
        imageUrl="/images/hills-bg.jpeg"
        title={documentInfo ? documentInfo.title : "Document Viewer"}
        subtitle={documentInfo ? "View and download document" : "Document information"}
        height="small"
      />
      
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              href="/" 
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Home
            </Link>
            <Link
              underline="hover"
              color="inherit"
              href="/covenants"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <DescriptionIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Documents
            </Link>
            <Typography color="text.primary">
              {documentInfo ? documentInfo.title : 'Document Not Found'}
            </Typography>
          </Breadcrumbs>
          
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/covenants')}
            sx={{ mb: 4 }}
          >
            Back to Documents
          </Button>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          ) : documentInfo ? (
            <SimplePDFViewer 
              pdfFile={documentInfo.documentUrl}
              title={documentInfo.title}
              description={documentInfo.description}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography>Document not found</Typography>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default DocumentViewer; 